import { Name, ZomentumEntities, ZomentumPSA } from "../../common";
import { EmailThreadMessage } from "../EmailThread";

export enum RecentActivityFeedType {
	All = "all",
	Email = "email",
	Comment = "comment",
	Task = "task",
	Meeting = "meeting",
	LogEmail = "log_email",
	LogCall = "log_call",
	History = "history",
	Opportunity = "opportunity",
	SyncEntityWithPSA = "sync_entity_with_psa"
}

export enum AuditTypes {
	Create = "create",
	Update = "update",
	Delete = "delete",
	ChangeSuggestionAccepted = "change_suggestion_accepted",
	ChangeSuggestionAutoAccepted = "change_suggestion_auto_accepted",
	ChangeSuggestionRejected = "change_suggestion_rejected",
	Shared = "shared",
	SharedRequested = "shared_requested",
	SharedAccepted = "shared_accepted",
	SharedRejected = "shared_rejected",
	Unshared = "unshared",
	Pushed = "pushed",
	Pulled = "pulled",
	Failed = "failed",
	Merged = "merged",
	McpCreate = "mcp_create",
	CopilotCreate = "copilot_create"
}

export enum SyncStatus {
	Success = "success",
	Failed = "failed"
}

export enum AuditSyncSource {
	ConnectWise = "connectwise",
	Zapier = "zapier",
	AutoTask = "autotask",
	Nylas = "nylas",
	Etilize = "etilize",
	DataImport = "data_import",
	DellQuoteImport = "dell_quote_import",
	Kaseya = "kaseya",
	Ingram = "ingram",
	TechData = "tech_data",
	DAndH = "d_and_h",
	Synnex = "synnex",
	Syncro = "syncro",
	Infusionsoft = "infusionsoft",
	Hubspot = "hubspot",
	CustomDistributor = "custom_distributor",
	HaloPSA = "haloPSA"
}

export const MarketingSyncSource = [
	AuditSyncSource.Infusionsoft,
	AuditSyncSource.Hubspot
];

export class SyncEntityRequest {
	entity_type = "";
	entity_id = "";
	sync_source: ZomentumPSA | null = null;
	sync_source_id?: string | null = null;
	should_export?: boolean | null = null;
	entity_name?: string;
	should_update_entity_name?: boolean;
}

export const AuditSyncSourceOptions = {
	[AuditSyncSource.ConnectWise]: "ConnectWise PSA",
	[AuditSyncSource.Zapier]: "Zapier",
	[AuditSyncSource.AutoTask]: "Autotask",
	[AuditSyncSource.Nylas]: "Nylas",
	[AuditSyncSource.Etilize]: "Etilize",
	[AuditSyncSource.DataImport]: "Data Import",
	[AuditSyncSource.DellQuoteImport]: "Dell Quote Import",
	[AuditSyncSource.Kaseya]: "Kaseya BMS",
	[AuditSyncSource.Ingram]: "Ingram",
	[AuditSyncSource.TechData]: "Tech Data",
	[AuditSyncSource.DAndH]: "D and H",
	[AuditSyncSource.Synnex]: "Synnex",
	[AuditSyncSource.Syncro]: "Syncro",
	[AuditSyncSource.Infusionsoft]: "Infusionsoft",
	[AuditSyncSource.Hubspot]: "HubSpot",
	[AuditSyncSource.CustomDistributor]: "Custom Distributor",
	[AuditSyncSource.HaloPSA]: "HaloPSA"
};

export const AuditEntityNameOptions: Partial<Record<ZomentumEntities, string>> =
	{
		[ZomentumEntities.Client]: "Client Company",
		[ZomentumEntities.Products]: "Products",
		[ZomentumEntities.Document]: "Document",
		[ZomentumEntities.DocumentBlock]: "Document Block",
		[ZomentumEntities.Comment]: "Note",
		[ZomentumEntities.Activities]: "Activities",
		[ZomentumEntities.Opportunity]: "Opportunity",
		[ZomentumEntities.Product]: "Product",
		[ZomentumEntities.ClientContact]: "Client Contact",
		[ZomentumEntities.SalesTask]: "Task",
		[ZomentumEntities.SalesMeeting]: "Meeting",
		[ZomentumEntities.Email]: "Email",
		[ZomentumEntities.EmailThread]: "Email Thread",
		[ZomentumEntities.EmailMessage]: "Email Message",
		[ZomentumEntities.SalesLogEmail]: "Email Log",
		[ZomentumEntities.SalesLogCall]: "Call Log",
		[ZomentumEntities.Assessment]: "Assessment",
		[ZomentumEntities.Qbr]: "QBR",
		[ZomentumEntities.CustomField]: "Custom Field",
		[ZomentumEntities.SalesActivity]: "Sales Activities",
		[ZomentumEntities.User]: "User",
		[ZomentumEntities.Pricing]: "Pricing",
		[ZomentumEntities.VendorResource]: "Vendor Resource",
		[ZomentumEntities.VendorPartner]: "Partner", // partner align entity,
		[ZomentumEntities.VendorCompany]: "Vendor Company",
		[ZomentumEntities.MarketplaceListing]: "Marketplace Listing",
		[ZomentumEntities.Vendors]: "Vendors",
		[ZomentumEntities.VendorCompanies]: "Companies",
		[ZomentumEntities.VendorTemplate]: "Template",
		[ZomentumEntities.VendorOpportunity]: "Opportunity",
		[ZomentumEntities.MarketplaceSKU]: "SKU",
		[ZomentumEntities.VendorClient]: "Vendor Client",
		[ZomentumEntities.SalesAutomation]: "Sales Automation",
		[ZomentumEntities.Supplier]: "Supplier"
	};

export enum ActivityAuthorType {
	User = "user",
	ClientUser = "client_user",
	Admin = "admin",
	Unknown = "unknown"
}

export enum ActivityType {
	Created = "created",
	Edited = "edited",
	Sent = "sent",
	Opened = "opened",
	Commented = "commented",
	Accepted = "accepted",
	SignInProgress = "sign_in_progress",
	LinkChanged = "link_changed",
	Signed = "signed",
	Won = "won",
	Lost = "lost"
}

export interface Attachment {
	file_name: string;
	path: string;
	url?: string | null;
}

export interface URLBody {
	fileId: string;
	url: string;
}

export class ActivityAuthor {
	name: Name = new Name();
}

export enum ActivityMessageType {
	TEXT = "plain_text",
	REDIRECT = "redirect_in_app"
}

export interface ActivityMessage {
	text: string;
	message_type: ActivityMessageType;
	value: string | null;
}

export class Activity {
	activity_type: ActivityType = ActivityType.Created;
	author: ActivityAuthor = new ActivityAuthor();
	author_id = "";
	author_type: ActivityAuthorType = ActivityAuthorType.User;
	created: Date = new Date();
	entity_id = "";
	entity_type: ZomentumEntities | null = null;
	id = "";
	message: ActivityMessage[] = new Array<ActivityMessage>();
	updated: Date = new Date();
	user_ip_address = "";
}

export class CommentAuthor {
	id = "";
	created: Date = new Date();
	updated: Date = new Date();
	email = "";
	name: Name = new Name();
	phone?: string;
	company_id = "";
	fc_token?: any;
	role?: any;
	bot_user_source?: any;
}

export class Comment {
	author_id = "";
	author_name: Name = new Name();
	body = "";
	created: Date = new Date();
	deleted = false;
	entity_id = "";
	entity_type: ZomentumEntities | null = null;
	id = "";
	in_reply_to = "";
	last_message_time_stamp: Date = new Date();
	pinned = false;
	type = "test";
	updated: Date = new Date();
	author: CommentAuthor = new CommentAuthor();
	attachments: Array<Attachment> = [];
}

export class AddRecentActivityFeedCommentRequest {
	body = "";
	entity_id = "";
	entity_type: ZomentumEntities | null = null;
	constructor(
		body: string,
		entity_id: string,
		entity_type: ZomentumEntities
	) {
		this.body = body;
		this.entity_id = entity_id;
		this.entity_type = entity_type;
	}
}

export interface RecentActivityFeed extends EmailThreadMessage, Comment {
	type: RecentActivityFeedType;
}

export interface RecentActivityFeedResponse {
	data: RecentActivityFeed[];
}

export interface CommentsResponse {
	data: Comment[];
}

export interface ActivitiesResponse {
	data: Activity[];
}

export interface RecentActivityItemView {
	entity_id: string;
	activity_type: ActivityType;
	author_id: string;
	author_type: ActivityAuthorType;
	updated: string;
	message: ActivityMessage[];
}

export interface RecentActivityResponse {
	data: RecentActivityItemView[];
	filtered_count_map?: Record<string, number> | null;
}

export class RecentActivityFeedCommentFormValues {
	body: string | null = null;
}
